import React, { useContext, useState, useEffect, useMemo } from "react";
import { LatLng } from "leaflet";
import { Pane, Marker, Polyline, useMap, Polygon, Circle } from "react-leaflet";
import ReactLeafletTextPath from "../ReactLeafletTextPath2";

import { ToolContext } from "../../context/ToolContext";
import { FormContext } from "../../context/FormContext";
import { iconMarker } from "../../utils/iconMarker";
import { stickyNoteIconMarker } from "../StickyNoteIconMarker";
import {
  doesAssetContainStatusObjects,
  doesAssetHaveStatus,
  doesAssetJoinCableHavingStatus,
} from "../../utils/networkFunctions";
import * as status from "../../constants/status";
import "./Asset.css";
import CounterBadge from "../CounterBadge";
import LctBadge from "../LctBadge";
import * as color from "../../constants/colors";
import { useSelector } from "react-redux";
import {
  hasFuse,
  hasKnownFuse,
  hasUnknownFuse,
  hasFuseWithAssignedValue,
} from "../../utils/networkFunctions";
import { calculateDistance, insertPointOnPath } from "../../utils/lineFunctions";
import AnnotationAsset from "./annotations/AnnotationAsset";
import { useAsset } from "./useAsset";
import Arrow from "./annotations/Arrow";
import NetworkElementTooltip from "../network-element-tooltip/NetworkElementTooltip";
import showNetworkElementTooltip from "../../utils/showNetworkElementTooltip";
import CommittedLoadTransformerWarningIcon from "../committed-load/TransformerWarningIcon";
import { ZIndex } from "../../constants/zindex";
import { useDefaultsChecker } from "../defaults-checker/useDefaultsChecker";

const Asset = ({
  asset,
  highlighted,
  onClick,
  label,
  onMove,
  reshapeCablePoints,
  isDragging,
  setIsDragging,
  renderer,
  fallbackRenderer,
  allGeometryAssets,
}) => {
  const { toolState, setToolState } = useContext(ToolContext);
  const { formState } = useContext(FormContext);
  const [errorMatch, setErrorMatch] = useState(false);
  const [warningMatch, setWarningMatch] = useState(false);
  const [lctIsEnabled, setLctIsEnabled] = useState(false);
  const [pipetteIsHovered, setPipetteIsHovered] = useState(false);
  const [offsetMarkers, setOffsetMarkers] = useState([]);
  const [offsetCables, setOffsetCables] = useState([]);
  const [offsetPolygons, setOffsetPolygons] = useState([]);
  const [offsetCircles, setOffsetCircles] = useState([]);
  const [offsetArrows, setOffsetArrows] = useState([]);

  const cables = useSelector((state) => state.network.present.cables);
  const transformers = useSelector((state) => state.network.present.transformers);

  const { clientSettings, groupedConnectionProperties } = formState;
  const {
    errors,
    mode,
    activeTool,
    ringfenced,
    ringfencedFiltered,
    matchedSearchIds,
    draggingArrowVertex,
    snapTargetAssetId,
    snapExcludedAssetId,
  } = toolState;

  const { checkAssetForDefaults } = useDefaultsChecker(clientSettings.features);

  const cableLabels = useSelector((state) => state.settings.showCableLabels);
  const cableLabelIds = useSelector((state) => state.settings.showCableLabelIds);
  const colourBlindSettingEnabled = useSelector((state) => state.settings.isColorBlindEnabled);
  const colourBlindEnabled =
    clientSettings.features.ColourBlindEnabled && colourBlindSettingEnabled;
  const statusSettingStyling = useSelector((state) => state.settings.isStatusStylingEnabled);
  const statusStyling = clientSettings.features.StatusStylingEnabled && statusSettingStyling;
  const easyClickTolerance = parseInt(clientSettings.general.easyClickTolerance);
  const iconSize = useSelector((state) => state.settings.iconSize);
  const lctIndicatorEnabled = useSelector((state) => state.settings.isLCTIndicatorEnabled);
  const showTooltip = showNetworkElementTooltip(
    useSelector((state) => state.settings.isNetworkElementTooltipEnabled),
    clientSettings.features.NetworkElementHoverTooltipEnabled,
    toolState.mode,
  );

  const cableZoomTrigger = parseInt(clientSettings.general.cableZoomTrigger);
  const cableZoomWeightMultiplier = parseInt(clientSettings.general.cableZoomWeightMultiplier);
  const applyCableZoom = () => {
    return map.getZoom() > cableZoomTrigger;
  };
  const ringfencedIds = ringfencedFiltered.map((asset) => asset.id);

  const map = useMap();
  const { canMoveObjects } = useAsset();

  const lctMap = [
    { name: "Generator", position: [-20, 0], type: "generator" },
    { name: "EV Chargepoint", position: [-5, -10], type: "evChargePoint" },
    { name: "Heat Pump", position: [10, -0], type: "heatPump" },
  ];

  const validElements = ["heatPump", "generator", "evChargePoint"];

  useEffect(() => {
    const erroredAssetIds = errors.messages
      ? errors.messages
          .filter((message) => message.level === "Error")
          .map((message) => message.link)
      : [];

    const warningAssetIds = errors.messages
      ? errors.messages
          .filter((message) => message.level === "Warning")
          .map((message) => message.link)
      : [];

    const filterWarningAssetIds = (a) => {
      return warningAssetIds.includes(a.id) || warningAssetIds.includes(a.wpdId);
    };

    const filterErrorAssetIds = (a) => {
      return erroredAssetIds.includes(a.id) || erroredAssetIds.includes(a.wpdId);
    };

    if (asset.groupedConnectionPoints && asset.groupedConnectionPoints.length > 0) {
      setWarningMatch(
        asset.groupedConnectionPoints.find((a) => filterWarningAssetIds(a))
          ? true
          : filterWarningAssetIds(asset),
      );
      setErrorMatch(
        asset.groupedConnectionPoints.find((a) => filterErrorAssetIds(a))
          ? true
          : filterErrorAssetIds(asset),
      );
    } else {
      setWarningMatch(filterWarningAssetIds(asset));
      setErrorMatch(filterErrorAssetIds(asset));
    }
  }, [errors]);

  useEffect(() => {
    asset?.groupedConnectionPoints?.some((g) => {
      return g.subGroupConnectionPoints.some((element) =>
        validElements.includes(element.styles.class),
      );
    }) &&
    iconSize === 3 &&
    clientSettings.features.LCTIndicatorEnabled &&
    lctIndicatorEnabled
      ? setLctIsEnabled(true)
      : setLctIsEnabled(false);
  }, [
    iconSize,
    clientSettings,
    lctIndicatorEnabled,
    asset?.groupedConnectionPoints?.flatMap((r) => r.subGroupConnectionPoints).length,
    asset,
  ]);

  const setSnapTarget = (assetId) => {
    const _toolState = { ...toolState, snapTargetAssetId: assetId };
    setToolState(_toolState);
  };

  const hasUnknownValue = (asset) => {
    let result = false;
    if (hasUnknownFuse(asset)) {
      result = true;
    }
    return result;
  };

  const hasUnassignedValue = (asset) => {
    let result = false;
    if (hasKnownFuse(asset) && !hasFuseWithAssignedValue(asset)) {
      result = true;
    }
    return result;
  };

  const getLineColour = (cable, defaultColour) => {
    if (statusStyling && cable.status === status.NEW) {
      return colourBlindEnabled
        ? clientSettings.general.stylingNewStatusAssetColourBlindColour
        : clientSettings.general.stylingNewStatusAssetColour;
    } else if (statusStyling && cable.status === status.REPLACEMENT) {
      return colourBlindEnabled
        ? clientSettings.general.stylingReplacementStatusAssetColourBlindColour
        : clientSettings.general.stylingReplacementStatusAssetColour;
    }
    return defaultColour;
  };

  const getMarkerStatus = (asset) => {
    if (statusStyling) {
      if (doesAssetHaveStatus(asset, status.NEW)) {
        return status.NEW;
      }
      if (doesAssetHaveStatus(asset, status.REPLACEMENT)) {
        return status.REPLACEMENT;
      }
      if (
        (asset.styles.name === "Node" || asset.styles.name === "Grouped Connection") &&
        !doesAssetContainStatusObjects(asset)
      ) {
        if (doesAssetJoinCableHavingStatus(asset, cables, status.NEW)) {
          return status.NEW;
        }
        if (doesAssetJoinCableHavingStatus(asset, cables, status.REPLACEMENT)) {
          return status.REPLACEMENT;
        }
      }
    }
    return status.EXISTING;
  };

  const getMarkerColour = (asset, defaultColour) => {
    const markerStatus = getMarkerStatus(asset);
    if (markerStatus === status.NEW) {
      return colourBlindEnabled ? "newColourblindMarkerColour" : "newMarkerColour";
    }
    if (markerStatus === status.REPLACEMENT) {
      return colourBlindEnabled ? "replacementColourblindMarkerColour" : "replacementMarkerColour";
    }
    return defaultColour;
  };

  const styleIcon = (asset) => {
    const hasDefaults = checkAssetForDefaults(asset);
    const isUnknown = hasUnknownValue(asset);
    const isUnassigned = hasUnassignedValue(asset);

    if (asset.styles.tool === "Line") {
      switch (true) {
        case pipetteIsHovered || highlighted || matchedSearchIds.includes(asset.id):
          return color.BLUE_HIGHLIGHT;
        case errorMatch:
          return "rgba(229, 30, 61, 1)";
        case warningMatch:
          return "rgba(250,183,33,1)";
        case hasDefaults:
          return "rgba(250,183,33,1)";
        case ringfencedIds.includes(asset.id):
          return "rgba(38, 183, 232, 1)";
        default:
          return getLineColour(asset, asset.styles.color);
      }
    }
    if (asset.styles.tool === "Marker") {
      const cssClass = (() => {
        if (asset.styles.type === "transformer" || !asset.groupedConnectionPoints)
          return asset.styles.class;

        if (asset.groupedConnectionPoints.length < 1) {
          if (asset.linkBox) return "linkbox";
          if (hasFuse(asset)) return "fuse";
          if (asset.pole && asset.pole.enabled) return "circle-dot";
          return "node";
        }

        if (asset.groupedConnectionPoints.length === 1) {
          return asset.groupedConnectionPoints[0].styles.class;
        }

        return "connection";
      })();

      const circleMarker =
        asset.styles.type !== "transformer"
          ? asset.groupedConnectionPoints
            ? asset.groupedConnectionPoints.length < 1
              ? !!asset.linkBox || hasFuse(asset)
              : true
            : asset.styles.circleMarker
          : asset.styles.circleMarker;

      let bgColor;
      if (highlighted || matchedSearchIds.includes(asset.id)) {
        bgColor = "primary";
      } else if (errorMatch) {
        bgColor = "danger";
      } else if (warningMatch) {
        bgColor = "warning";
      } else if (hasDefaults || isUnassigned || isUnknown) {
        bgColor = "warning";
      } else if (ringfencedIds.includes(asset.id)) {
        bgColor = "primary";
      } else {
        bgColor = getMarkerColour(asset, "dark");
      }

      asset = {
        ...asset,
        styles: {
          ...asset.styles,
          cssClass: cssClass,
          circleMarker: circleMarker,
          bgColor: bgColor,
        },
      };

      let nodeZoomMultiplier = applyCableZoom() ? cableZoomWeightMultiplier : 1;

      const id = asset.styles.type === "transformer" ? createTransformerId() : "";

      return iconMarker(
        asset,
        mode,
        activeTool,
        iconSize,
        nodeZoomMultiplier,
        true,
        lctIsEnabled,
        colourBlindEnabled,
        statusStyling,
        groupedConnectionProperties,
        id,
      );
    }
    if (asset.styles.tool === "Annotation") {
      if (asset.styles.class === "sticky-note") {
        return stickyNoteIconMarker(asset, highlighted || ringfencedIds.includes(asset.id));
      }
    }
  };
  const createTransformerId = () => {
    const txId = transformers.findIndex((t) => t.id === asset.id);
    return asset.annotation || asset.substationId || `Transformer ${txId + 1}`;
  };

  const countGroupedConnections = (asset) => {
    if (!asset.groupedConnectionPoints || !asset.groupedConnectionPoints.length) return;
    return asset.groupedConnectionPoints.length > 1
      ? asset.groupedConnectionPoints.reduce((a, c) => a + Number(c.count), 0)
      : asset.groupedConnectionPoints[0].count;
  };

  const groupedConnectionsCount = countGroupedConnections(asset);

  const textPathText = (asset) => {
    const cableType = `${
      asset.autoSelect && asset.cableTypeAssigned !== undefined
        ? `${asset.cableTypeAssigned !== null ? asset.cableTypeAssigned : "AUTO"} - ${
            asset.overrideLength > 0
              ? asset.overrideLength
              : asset.length && Number(asset.length).toFixed(2)
          }m`
        : `${asset.cableType !== null ? asset.cableType : "AUTO"} - ${
            asset.overrideLength > 0
              ? asset.overrideLength
              : asset.length && Number(asset.length).toFixed(2)
          }m`
    } - `;

    return cableLabelIds ? `${asset.feederSequence || asset.id} - ${cableType}` : `${cableType}`;
  };

  const cableWeight = (asset) => {
    let weight = 2;

    if (!asset || !asset.cableGroup) return weight;

    if (asset.cableGroup === "mains-underground" || asset.cableGroup === "mains-overhead") {
      weight = 4;
    }

    if (applyCableZoom()) {
      weight = weight * cableZoomWeightMultiplier;
    }

    return weight;
  };

  const cableDash = (asset, defaultDashLength, defaultSpacingLength, cableType) => {
    let dash = "";

    if (!asset || !asset.cableGroup || !defaultDashLength || !defaultSpacingLength || !cableType)
      return dash;

    let dashLength = defaultDashLength;
    let spacingLength = defaultSpacingLength;

    if (applyCableZoom()) {
      dashLength = dashLength * cableZoomWeightMultiplier;
      spacingLength = spacingLength * cableZoomWeightMultiplier;
    }

    if (cableType === "label" && asset.cableGroup.endsWith("underground")) {
      return `${dashLength} ${spacingLength}`;
    }

    if (cableType === "line" && asset.cableGroup.endsWith("underground")) {
      return `${dashLength} ${spacingLength}`;
    }

    if (
      cableType === "grouped-connection-points" &&
      (asset.cableGroup === "mains-overhead" || asset.cableGroup === "mains-underground")
    ) {
      return `${dashLength} ${spacingLength}`;
    }

    return dash;
  };

  const textPathOffset = (asset, defaultOffset) => {
    if (!asset) return defaultOffset;

    let offset = defaultOffset;

    if (applyCableZoom()) {
      offset = offset + cableZoomWeightMultiplier * 2;
    }

    return offset;
  };

  const textPathFontSize = (asset, defaultFontSize) => {
    if (!asset) return defaultFontSize;

    let fontSize = defaultFontSize;

    if (applyCableZoom()) {
      fontSize = fontSize + cableZoomWeightMultiplier;
    }

    return fontSize;
  };

  const subGroupConnectionPoints = (asset) => {
    let yo = 0;
    asset?.groupedConnectionPoints?.forEach((gcp) => {
      if (gcp?.subGroupConnectionPoints) yo += gcp?.subGroupConnectionPoints?.length;
    });
    return yo;
  };

  const hasLcts = (asset) => {
    return lctMap.some((lct) => hasLct(asset, lct.name));
  };

  const hasLct = (asset, lctType) => {
    return (
      asset.groupedConnectionPoints &&
      asset.groupedConnectionPoints.find((gcp) =>
        gcp?.subGroupConnectionPoints.some((sgcp) => sgcp.styles.name === lctType),
      )
    );
  };

  const handleDrag = (event, movingAsset) => {
    const markerId = movingAsset.id;
    let lat, lng;
    let originalLatLng;
    switch (movingAsset.styles.layerType) {
      case "marker":
        ({ lat, lng } = event.target.getLatLng());
        originalLatLng = { lat: event.oldLatLng.lat, lng: event.oldLatLng.lng };
        break;
      case "polygon":
        const latLngs = event.target.getLatLngs();
        // Flatten in case of nested arrays (e.g., polygons)
        const firstLatLng = Array.isArray(latLngs[0]) ? latLngs[0][0] : latLngs[0];
        ({ lat, lng } = firstLatLng);
        originalLatLng = { lat: movingAsset.geometry[0].lat, lng: movingAsset.geometry[0].lng };
        break;
      case "circle":
        const transformableRectangleLatLngs = event.target.getLatLngs()[0];
        lat =
          (transformableRectangleLatLngs[0].lat +
            transformableRectangleLatLngs[1].lat +
            transformableRectangleLatLngs[2].lat +
            transformableRectangleLatLngs[3].lat) /
          4;
        lng =
          (transformableRectangleLatLngs[0].lng +
            transformableRectangleLatLngs[1].lng +
            transformableRectangleLatLngs[2].lng +
            transformableRectangleLatLngs[3].lng) /
          4;
        originalLatLng = {
          lat: movingAsset.geometry.center.lat,
          lng: movingAsset.geometry.center.lng,
        };
        break;
      default:
        // fallback to event.latlng if available
        ({ lat, lng } = event.latlng || {});
        originalLatLng = { lat: event.oldLatLng.lat, lng: event.oldLatLng.lng };
        break;
    }

    const assets = ringfenced;

    const offsetMarkers = assets
      .filter((asset) => asset.id !== markerId && !asset.geometry.length && !asset.geometry.radius)
      .map((asset) => {
        const geometry = allGeometryAssets.find((p) => p.asset.id === asset.id).geometry;
        const offsetLat = geometry.lat + (lat - originalLatLng.lat);
        const offsetLng = geometry.lng + (lng - originalLatLng.lng);
        return {
          id: asset.id,
          icon: styleIcon(asset),
          position: new LatLng(offsetLat, offsetLng),
        };
      });

    const offsetCables = assets
      .filter(
        (asset) =>
          asset.id !== markerId && asset.geometry.length > 0 && asset.styles.layerType !== "arrow",
      )
      .map((asset) => {
        const geometry = allGeometryAssets.find((p) => p.asset.id === asset.id).geometry;
        return {
          id: asset.id,
          position: geometry.map((p) => {
            return {
              lat: p.lat + (lat - originalLatLng.lat),
              lng: p.lng + (lng - originalLatLng.lng),
            };
          }),
        };
      });

    const offsetArrows = assets
      .filter(
        (asset) =>
          asset.id !== markerId && asset.styles.layerType === "arrow" && asset.geometry.length > 0,
      )
      .map((asset) => {
        const geometry = allGeometryAssets.find((p) => p.asset.id === asset.id).geometry;
        return {
          id: asset.id,
          position: geometry.map((p) => {
            return {
              lat: p.lat + (lat - originalLatLng.lat),
              lng: p.lng + (lng - originalLatLng.lng),
            };
          }),
        };
      });

    const indirectReshapeCables = allGeometryAssets
      .filter((cable) => [cable.asset.startAssetId, cable.asset.endAssetId].includes(markerId))
      .filter(
        (cable) =>
          !(
            assets.some((p) => p.id === cable.asset.startAssetId) &&
            assets.some((p) => p.id === cable.asset.endAssetId)
          ),
      )
      .map((cable) => {
        const start = cable.geometry[0];
        const end = cable.geometry[cable.geometry.length - 1];
        const anchors = reshapeCablePoints
          .filter((p) => p.assetId === cable.asset.id)
          .map((p) => p.position);

        if (anchors.length === 0) {
          if (calculateDistance(start, originalLatLng) < calculateDistance(end, originalLatLng)) {
            return {
              id: cable.asset.id,
              position: [{ lat, lng }, end],
            };
          }
          return {
            id: cable.asset.id,
            position: [start, { lat, lng }],
          };
        }
        if (calculateDistance(start, originalLatLng) < calculateDistance(end, originalLatLng)) {
          return {
            id: cable.asset.id,
            position: insertPointOnPath(cable.geometry, anchors, start, {
              lat,
              lng,
            }),
          };
        }
        return {
          id: cable.asset.id,
          position: insertPointOnPath(cable.geometry, anchors, end, {
            lat,
            lng,
          }),
        };
      });

    const offsetPolygons = assets
      .filter(
        (asset) =>
          asset.id !== markerId && asset.geometry.length > 0 && asset.styles.class === "rectangle",
      )
      .map((asset) => {
        const geometry = allGeometryAssets.find((p) => p.asset.id === asset.id).geometry;
        // Calculate offset for each point in the rectangle
        return {
          id: asset.id,
          geometry: geometry.map((p) => ({
            lat: p.lat + (lat - originalLatLng.lat),
            lng: p.lng + (lng - originalLatLng.lng),
          })),
          styles: asset.styles,
        };
      });

    const offsetCircles = assets
      .filter((asset) => asset.id !== markerId && asset.styles.class === "circle")
      .map((asset) => {
        const geometry = allGeometryAssets.find((p) => p.asset.id === asset.id).geometry;
        // Calculate offset for each point in the circle
        return {
          id: asset.id,
          geometry: {
            center: {
              lat: geometry.center.lat + (lat - originalLatLng.lat),
              lng: geometry.center.lng + (lng - originalLatLng.lng),
            },
            radius: geometry.radius,
          },
          styles: asset.styles,
        };
      });

    setOffsetPolygons(offsetPolygons);
    setOffsetCircles(offsetCircles);
    setOffsetMarkers(offsetMarkers);
    setOffsetCables(offsetCables.concat(indirectReshapeCables));
    setOffsetArrows(offsetArrows);
    setIsDragging(true);
  };

  const dragEnd = (e, asset) => {
    setOffsetMarkers([]);
    setOffsetCables([]);
    setOffsetPolygons([]);
    setOffsetCircles([]);
    setOffsetArrows([]);
    setIsDragging(false);
    onMove(e, asset);
  };

  const icon = useMemo(
    () => styleIcon(asset),
    [
      applyCableZoom(),
      asset,
      highlighted,
      ringfencedFiltered,
      matchedSearchIds,
      iconSize,
      errorMatch,
      warningMatch,
      lctIsEnabled,
      colourBlindEnabled,
      statusStyling,
    ],
  );

  const getIconPosition = () => {
    switch (iconSize) {
      case 3:
        return [20, 20];
      case 2:
        return [15, 19];
      case 1:
        return [7, 18];
    }
  };

  const getWarningIconPosition = () => {
    switch (iconSize) {
      case 3:
        return [-28, 20];
      case 2:
        return [-23, 19];
      case 1:
        return [-15, 18];
    }
  };

  const iconPosition = getIconPosition();
  const warningIconPosition = getWarningIconPosition();

  const countValue = (asset) => {
    return asset.styles.type === "transformer" ? 0 : 1;
  };

  const tooltipOffset = (() => {
    switch (iconSize) {
      case 1:
        return [-40, 0];
      case 2:
        return [-47, 0];
      case 3:
        return [-55, 0];
      default:
        return [-23, 0];
    }
  })();

  const arrowSnappingActive = () =>
    draggingArrowVertex || (mode === "draw" && activeTool.class === "arrow");

  return (
    <>
      {asset.styles.tool === "Line" && (
        <>
          {cableLabels ? (
            <ReactLeafletTextPath
              key={asset.id}
              id={asset.id}
              positions={asset.geometry}
              color={styleIcon(asset)}
              weight={cableWeight(asset)}
              dashArray={cableDash(asset, 4, 6, "label")}
              snapIgnore={true}
              text={mode === "remove" ? null : textPathText(asset)}
              repeat
              below
              center
              offset={textPathOffset(asset, 14)}
              attributes={{
                "font-size": textPathFontSize(asset, 10),
              }}
              styles={{}}
              eventHandlers={{
                click: (e) => {
                  onClick(asset, e);
                },
                mouseover: (e) => {
                  if (mode === "pipette") {
                    setPipetteIsHovered(true);
                  }
                },
                mouseout: (e) => {
                  if (pipetteIsHovered) {
                    setPipetteIsHovered(false);
                  }
                },
              }}
              renderer={fallbackRenderer}
            >
              <NetworkElementTooltip asset={asset} sticky enabled={showTooltip} offset={[-30, 0]} />
            </ReactLeafletTextPath>
          ) : (
            <Polyline
              key={asset.id}
              id={asset.id}
              positions={asset.geometry}
              snapIgnore={true}
              pathOptions={{
                renderer: renderer,
                weight: cableWeight(asset),
                dashArray: cableDash(asset, 4, 6, "line"),
                color: styleIcon(asset),
              }}
              eventHandlers={{
                click: (e) => {
                  onClick(asset, e);
                  if (e.originalEvent) {
                    e.originalEvent.clickedCableOnCanvas = true;
                  }
                },
                mouseover: (e) => {
                  if (mode === "pipette") {
                    setPipetteIsHovered(true);
                  }
                },
                mouseout: (e) => {
                  if (pipetteIsHovered) {
                    setPipetteIsHovered(false);
                  }
                },
              }}
            >
              <NetworkElementTooltip asset={asset} sticky enabled={showTooltip} offset={[-30, 0]} />
            </Polyline>
          )}
          {asset.groupedConnectionPoints.length > 0 && (
            <Pane
              name={`distributed-connection-${asset.id}`}
              style={{ zIndex: ZIndex.assetDistributedConnection }}
            >
              <Polyline
                id={asset.id}
                positions={asset.geometry}
                pathOptions={{
                  weight: cableWeight(asset),
                  dashArray: cableDash(asset, 4, 8, "grouped-connection-points"),
                  color: `${pipetteIsHovered ? color.BLUE_HIGHLIGHT : "#ffffff"}`,
                }}
                snapIgnore={true}
                eventHandlers={{
                  click: (e) => {
                    onClick(asset, e);
                  },
                  mouseover: (e) => {
                    if (mode === "pipette") {
                      setPipetteIsHovered(true);
                    }
                  },
                  mouseout: (e) => {
                    if (pipetteIsHovered) {
                      setPipetteIsHovered(false);
                    }
                  },
                }}
              >
                <NetworkElementTooltip
                  asset={asset}
                  sticky
                  enabled={showTooltip}
                  offset={[-30, 0]}
                />
              </Polyline>
            </Pane>
          )}
        </>
      )}
      {asset.styles.tool === "Marker" && (
        <>
          <Marker
            id={asset.id}
            position={asset.geometry}
            icon={icon}
            draggable={canMoveObjects(asset.id)}
            eventHandlers={{
              click: () => {
                onClick(asset);
              },
              drag: (e) => handleDrag(e, asset),
              dragend: (e) => dragEnd(e, asset),
            }}
          >
            <NetworkElementTooltip asset={asset} offset={tooltipOffset} enabled={showTooltip} />
          </Marker>
          <CommittedLoadTransformerWarningIcon
            asset={asset}
            position={warningIconPosition}
            isDragging={isDragging}
          />
          {asset.groupedConnectionPoints &&
            groupedConnectionsCount > countValue(asset) &&
            !isDragging && (
              <CounterBadge
                asset={asset}
                value={groupedConnectionsCount}
                position={iconPosition}
                popup={true}
              />
            )}
          {!isDragging && lctIsEnabled && (
            <>
              {!hasLcts(asset) && (
                <CounterBadge
                  asset={asset}
                  value={subGroupConnectionPoints(asset)}
                  position={iconPosition}
                  popup={false}
                />
              )}
              {lctMap.map((lct) => (
                <React.Fragment key={lct.name}>
                  {hasLct(asset, lct.name) && (
                    <LctBadge
                      asset={asset}
                      position={lct.position}
                      lctType={lct.type}
                      onClick={onClick}
                      showTooltip={showTooltip}
                    />
                  )}
                </React.Fragment>
              ))}
            </>
          )}
          {!isDragging && label && (
            <CounterBadge asset={asset} label={label} position={[20, 20]} popup={true} />
          )}
        </>
      )}
      {asset.styles.tool === "Annotation" && (
        <AnnotationAsset
          annotation={asset}
          icon={icon}
          draggable={canMoveObjects(asset.id)}
          isDragging={isDragging}
          highlighted={highlighted}
          arrowSnappingActive={arrowSnappingActive()}
          easyClickTolerance={easyClickTolerance}
          eventHandlers={{
            click: () => {
              onClick(asset);
            },
            mouseover: (e) => {
              if (asset.styles.arrowSnapTarget && arrowSnappingActive()) {
                if (asset.id !== snapExcludedAssetId) {
                  setSnapTarget(asset.id);
                }
              }
            },
            mouseout: (e) => {
              if (asset.styles.arrowSnapTarget) {
                if (!!snapTargetAssetId) {
                  setSnapTarget("");
                }
              }
            },
            drag: (e) => handleDrag(e, asset),
            dragend: (e) => dragEnd(e, asset),
          }}
        />
      )}
      {offsetMarkers.map((marker) => (
        <Marker
          key={`${marker.id}-drag`}
          icon={marker.icon}
          position={marker.position}
          opacity={0.5}
          zIndexOffset={4000}
        />
      ))}
      {offsetCables.map((cable) => (
        <Polyline
          key={`${cable.id}-drag`}
          positions={cable.position}
          pathOptions={{
            weight: 1,
            color: color.BLUE_HIGHLIGHT,
          }}
          opacity={0.5}
          zIndexOffset={4000}
        />
      ))}
      {offsetPolygons.map((polygon) => (
        <Polygon
          key={`${polygon.id}-drag`}
          positions={polygon.geometry}
          pathOptions={polygon.styles}
          opacity={0.5}
          easyClickTolerance={easyClickTolerance}
          zIndexOffset={4000}
        />
      ))}
      {offsetCircles.map((circle) => (
        <Circle
          key={`${circle.id}-drag`}
          center={circle.geometry.center}
          radius={circle.geometry.radius}
          pathOptions={circle.styles}
          opacity={0.5}
          easyClickTolerance={easyClickTolerance}
          zIndexOffset={4000}
        />
      ))}
      {offsetArrows.map((arrow) => (
        <Arrow
          key={`${arrow.id}-drag`}
          positions={arrow.position}
          pathOptions={{
            color: color.BLUE_HIGHLIGHT,
          }}
          opacity={0.5}
          zIndexOffset={4000}
          zoomLevel={map.getZoom()}
          easyClickTolerance={easyClickTolerance}
          interactive={false}
        />
      ))}
    </>
  );
};

export default Asset;
